import {
  LgmCattleTargetWeightsByTypeCode,
  HiqLgmTypeCodeNames,
  HiqLrpTypeCodeNames,
  LgmSwineTargetWeightsByCommodityTypeCode,
  RmaLgmTypeCodes,
  RmaLgmTypeCode,
} from '@harvestiq/constants';
import { z } from '@harvestiq/zod';

// Shape of { series: 'Some Series', 'Sep 2024': 2341.12, ...}
export const legacyLgmSeriesSchema = z.record(
  z.string(),
  z.string().or(z.number()).nullable()
);
export type LegacyLgmSeries = z.infer<typeof legacyLgmSeriesSchema>;

/**
 * Adds zod conditional validation of the LGM target fields of a schema based on the commodity type field.
 */
export const addLgmTargetSchemaValidation = <SchemaT extends z.ZodTypeAny>({
  schema,
  // TODO: Remove reliance on commodity type name and use only commodity type code
  lgmCommodityTypeNameField,
  lgmTypeCodeField,
}: {
  schema: SchemaT;
  lgmCommodityTypeNameField?: string;
  lgmTypeCodeField?: string;
}) => {
  if (!lgmCommodityTypeNameField && !lgmTypeCodeField) {
    throw new Error(
      'lgmCommodityTypeNameField or lgmTypeCodeField  is required'
    );
  }

  return schema.superRefine((data: any, ctx: z.RefinementCtx) => {
    const commodityTypeName = lgmCommodityTypeNameField
      ? (data[lgmCommodityTypeNameField] as HiqLgmTypeCodeNames)
      : undefined;

    const typeCodeFromData = lgmTypeCodeField
      ? z.nativeEnum(RmaLgmTypeCodes).parse(data[lgmTypeCodeField].toString())
      : undefined;

    const liveCattleTargetCwtPerHead = data.liveCattleTargetCwtPerHead as
      | number
      | undefined;
    const feederCattleTargetCwtPerHead = data.feederCattleTargetCwtPerHead as
      | number
      | undefined;
    const cornTargetBuPerHead = data.cornTargetBuPerHead as number | undefined;

    const cattleCommodityTypeNameUsed =
      commodityTypeName &&
      (commodityTypeName === HiqLgmTypeCodeNames.CALF_FINISHING ||
        commodityTypeName === HiqLgmTypeCodeNames.YEARLING_FINISHING);
    const cattleTypeCodeUsed =
      typeCodeFromData &&
      (typeCodeFromData === RmaLgmTypeCodes.CALF_FINISHING ||
        typeCodeFromData === RmaLgmTypeCodes.YEARLING_FINISHING);

    if (cattleCommodityTypeNameUsed || cattleTypeCodeUsed) {
      let typeCode:
        | typeof RmaLgmTypeCodes.CALF_FINISHING
        | typeof RmaLgmTypeCodes.YEARLING_FINISHING
        | undefined;

      if (
        commodityTypeName === HiqLgmTypeCodeNames.CALF_FINISHING ||
        typeCodeFromData === RmaLgmTypeCodes.CALF_FINISHING
      ) {
        typeCode = RmaLgmTypeCodes.CALF_FINISHING;
      } else {
        typeCode = RmaLgmTypeCodes.YEARLING_FINISHING;
      }

      const liveCattleTargetWeightMin =
        LgmCattleTargetWeightsByTypeCode[typeCode].liveCattle.min;
      const liveCattleTargetWeightMax =
        LgmCattleTargetWeightsByTypeCode[typeCode].liveCattle.max;
      const feederCattleTargetWeightMin =
        LgmCattleTargetWeightsByTypeCode[typeCode].feederCattle.min;
      const feederCattleTargetWeightMax =
        LgmCattleTargetWeightsByTypeCode[typeCode].feederCattle.max;
      const cornTargetWeightMin =
        LgmCattleTargetWeightsByTypeCode[typeCode].corn.min;
      const cornTargetWeightMax =
        LgmCattleTargetWeightsByTypeCode[typeCode].corn.max;

      if (
        liveCattleTargetCwtPerHead !== undefined &&
        (liveCattleTargetCwtPerHead < liveCattleTargetWeightMin.toNumber() ||
          liveCattleTargetCwtPerHead > liveCattleTargetWeightMax.toNumber())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `liveCattleTargetCwtPerHead for commodity ${
            commodityTypeName ?? typeCodeFromData
          } must be within ${liveCattleTargetWeightMin} and ${liveCattleTargetWeightMax}`,
        });
      }

      if (
        feederCattleTargetCwtPerHead !== undefined &&
        (feederCattleTargetCwtPerHead <
          feederCattleTargetWeightMin.toNumber() ||
          feederCattleTargetCwtPerHead > feederCattleTargetWeightMax.toNumber())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `feederCattleTargetCwtPerHead for commodity ${
            commodityTypeName ?? typeCodeFromData
          } must be within ${feederCattleTargetWeightMin} and ${feederCattleTargetWeightMax}`,
        });
      }

      if (
        cornTargetBuPerHead !== undefined &&
        (cornTargetBuPerHead < cornTargetWeightMin.toNumber() ||
          cornTargetBuPerHead > cornTargetWeightMax.toNumber())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cornTargetBuPerHead for commodity ${
            commodityTypeName ?? typeCodeFromData
          } must be within ${cornTargetWeightMin} and ${cornTargetWeightMax}`,
        });
      }
    } else {
      if (liveCattleTargetCwtPerHead) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `liveCattleTargetCwtPerHead is not allowed for commodity ${
            commodityTypeName ?? typeCodeFromData
          }`,
          path: ['liveCattleTargetCwtPerHead'],
        });
      }
      if (feederCattleTargetCwtPerHead) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `feederCattleTargetCwtPerHead is not allowed for commodity ${
            commodityTypeName ?? typeCodeFromData
          }`,
          path: ['feederCattleTargetCwtPerHead'],
        });
      }
      if (cornTargetBuPerHead) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cornTargetBuPerHead is not allowed for commodity ${
            commodityTypeName ?? typeCodeFromData
          }`,
          path: ['cornTargetBuPerHead'],
        });
      }
    }
  });
};
